/**
 * Mock team member data for StarNexus platform
 * @module data/starnexus/mockTeamMembers
 */

import type { TeamMember, UserRole, Team } from '../../types/starnexus';

// Helper to create dates relative to now
const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

const hoursAgo = (hours: number): Date => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date;
};

// Mock teams
export const mockTeams: Team[] = [
  {
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    description: 'Retail data analytics and reporting team',
    owners: ['yyangzam'],
    members: ['yyangzam', 'user-002', 'user-003', 'user-004', 'user-005', 'user-006', 'user-007', 'user-008'],
    createdAt: '2024-06-01T10:00:00Z',
    createdBy: 'yyangzam'
  },
  {
    teamId: 'team-002',
    teamName: 'Customer Experience',
    description: 'Customer feedback and support analytics',
    owners: ['user-009'],
    members: ['user-009', 'user-010', 'user-011', 'user-012', 'user-013', 'user-014'],
    createdAt: '2024-07-15T10:00:00Z',
    createdBy: 'user-009'
  },
  {
    teamId: 'team-003',
    teamName: 'Operations',
    description: 'Operations and logistics data management',
    owners: ['user-015'],
    members: ['user-015', 'user-016', 'user-017', 'user-018', 'user-019', 'user-020', 'user-021', 'user-022', 'user-023', 'user-024'],
    createdAt: '2024-05-20T10:00:00Z',
    createdBy: 'user-015'
  },
  {
    teamId: 'team-004',
    teamName: 'Marketing',
    description: 'Marketing analytics and campaign tracking',
    owners: ['user-025'],
    members: ['user-025', 'user-026', 'user-027', 'user-028', 'user-029'],
    createdAt: '2024-08-01T10:00:00Z',
    createdBy: 'user-025'
  },
  {
    teamId: 'team-005',
    teamName: 'Finance',
    description: 'Financial data processing and reporting',
    owners: ['user-030'],
    members: ['user-030', 'user-031', 'user-032', 'user-033'],
    createdAt: '2024-09-10T10:00:00Z',
    createdBy: 'user-030'
  },
  {
    teamId: 'team-006',
    teamName: 'Human Resources',
    description: 'HR data and employee analytics',
    owners: ['user-034'],
    members: ['user-034', 'user-035', 'user-036'],
    createdAt: '2024-10-05T10:00:00Z',
    createdBy: 'user-034'
  }
];

// Mock team members
export const mockTeamMembers: TeamMember[] = [
  // Retail Analytics Team
  {
    userId: 'user-001',
    name: 'Alice Chen',
    email: 'alice@example.com',
    role: 'admin',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    datasetAccess: ['ds-001', 'ds-002', 'ds-006'],
    lastActive: hoursAgo(0.5),
    joinedAt: daysAgo(365)
  },
  {
    userId: 'user-009',
    name: 'Ivan Rodriguez',
    email: 'ivan@example.com',
    role: 'manager',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    datasetAccess: ['ds-001', 'ds-002'],
    lastActive: hoursAgo(2),
    joinedAt: daysAgo(200)
  },
  {
    userId: 'user-010',
    name: 'Julia Kim',
    email: 'julia@example.com',
    role: 'viewer',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    datasetAccess: ['ds-001'],
    lastActive: daysAgo(1),
    joinedAt: daysAgo(30)
  },

  // Customer Experience Team
  {
    userId: 'user-003',
    name: 'Carol Davis',
    email: 'carol@example.com',
    role: 'manager',
    teamId: 'team-002',
    teamName: 'Customer Experience',
    datasetAccess: ['ds-003', 'ds-010', 'ds-015'],
    lastActive: hoursAgo(1),
    joinedAt: daysAgo(180)
  },
  {
    userId: 'user-011',
    name: 'Kevin Patel',
    email: 'kevin@example.com',
    role: 'viewer',
    teamId: 'team-002',
    teamName: 'Customer Experience',
    datasetAccess: ['ds-003', 'ds-010'],
    lastActive: hoursAgo(4),
    joinedAt: daysAgo(60)
  },

  // Operations Team
  {
    userId: 'user-004',
    name: 'David Lee',
    email: 'david@example.com',
    role: 'manager',
    teamId: 'team-003',
    teamName: 'Operations',
    datasetAccess: ['ds-004', 'ds-011', 'ds-013'],
    lastActive: hoursAgo(0.1),
    joinedAt: daysAgo(300)
  },
  {
    userId: 'user-008',
    name: 'Henry Brown',
    email: 'henry@example.com',
    role: 'viewer',
    teamId: 'team-003',
    teamName: 'Operations',
    datasetAccess: ['ds-013'],
    lastActive: hoursAgo(3),
    joinedAt: daysAgo(45)
  },
  {
    userId: 'user-012',
    name: 'Lisa Wang',
    email: 'lisa@example.com',
    role: 'viewer',
    teamId: 'team-003',
    teamName: 'Operations',
    datasetAccess: ['ds-004', 'ds-011'],
    lastActive: hoursAgo(6),
    joinedAt: daysAgo(90)
  },

  // Marketing Team
  {
    userId: 'user-005',
    name: 'Emma Wilson',
    email: 'emma@example.com',
    role: 'manager',
    teamId: 'team-004',
    teamName: 'Marketing',
    datasetAccess: ['ds-005', 'ds-008', 'ds-014'],
    lastActive: hoursAgo(0.2),
    joinedAt: daysAgo(250)
  },
  {
    userId: 'user-013',
    name: 'Michael Scott',
    email: 'michael@example.com',
    role: 'viewer',
    teamId: 'team-004',
    teamName: 'Marketing',
    datasetAccess: ['ds-005', 'ds-008'],
    lastActive: daysAgo(2),
    joinedAt: daysAgo(120)
  },

  // Finance Team
  {
    userId: 'user-006',
    name: 'Frank Miller',
    email: 'frank@example.com',
    role: 'manager',
    teamId: 'team-005',
    teamName: 'Finance',
    datasetAccess: ['ds-007', 'ds-012'],
    lastActive: hoursAgo(4),
    joinedAt: daysAgo(400)
  },
  {
    userId: 'user-014',
    name: 'Nancy Drew',
    email: 'nancy@example.com',
    role: 'viewer',
    teamId: 'team-005',
    teamName: 'Finance',
    datasetAccess: ['ds-007'],
    lastActive: hoursAgo(8),
    joinedAt: daysAgo(150)
  },

  // Human Resources Team
  {
    userId: 'user-007',
    name: 'Grace Taylor',
    email: 'grace@example.com',
    role: 'manager',
    teamId: 'team-006',
    teamName: 'Human Resources',
    datasetAccess: ['ds-009'],
    lastActive: daysAgo(2),
    joinedAt: daysAgo(500)
  },
  {
    userId: 'user-015',
    name: 'Oscar Martinez',
    email: 'oscar@example.com',
    role: 'viewer',
    teamId: 'team-006',
    teamName: 'Human Resources',
    datasetAccess: ['ds-009'],
    lastActive: daysAgo(3),
    joinedAt: daysAgo(80)
  },

  // Cross-team admin
  {
    userId: 'user-002',
    name: 'Bob Smith',
    email: 'bob@example.com',
    role: 'admin',
    teamId: 'team-001',
    teamName: 'Retail Analytics',
    datasetAccess: ['ds-001', 'ds-002', 'ds-003', 'ds-004', 'ds-005', 'ds-006'],
    lastActive: hoursAgo(0.5),
    joinedAt: daysAgo(600)
  }
];

// Current user (for testing role-based access)
export const currentUser: TeamMember = {
  userId: 'user-001',
  name: 'Alice Chen',
  email: 'alice@example.com',
  role: 'admin',
  teamId: 'team-001',
  teamName: 'Retail Analytics',
  datasetAccess: ['ds-001', 'ds-002', 'ds-006'],
  lastActive: new Date(),
  joinedAt: daysAgo(365)
};

// Export helper functions
export const getMemberById = (userId: string): TeamMember | undefined => {
  return mockTeamMembers.find((m) => m.userId === userId);
};

export const getMembersByTeam = (teamId: string): TeamMember[] => {
  return mockTeamMembers.filter((m) => m.teamId === teamId);
};

export const getMembersByRole = (role: UserRole): TeamMember[] => {
  return mockTeamMembers.filter((m) => m.role === role);
};

export const getTeamById = (teamId: string): Team | undefined => {
  return mockTeams.find((t) => t.teamId === teamId);
};

export const getMembersWithDatasetAccess = (
  datasetId: string
): TeamMember[] => {
  return mockTeamMembers.filter((m) => m.datasetAccess?.includes(datasetId));
};
